// ============================================================
//  Rohdaten → Dataset. EINE Funktion für beide Quellen:
//  · der deterministische Generator (Demo-Kohorten)
//  · der Seed-Loader (echte Exportdateien aus scripts/seed-organisation.mjs)
//
//  Hier passiert die eigentliche Arbeit der App: Konten klassifizieren
//  (classification.ts über das Regelwerk), Personen finalisieren (R1/R2, AP1),
//  Maßnahmen aus den entfernbaren Rollen ableiten (R3, Aufwand aus der Nutzung).
//  Wer die Logik ändert, ändert sie für beide Quellen — das ist Absicht.
// ============================================================

import type {
  Account, AuthValue, CompositeRole, Driver, EngineMetric, Lever, LeverCluster, LicenseClass,
  Person, Role, SingleRole, SystemClass, TraceState,
} from "./types";
import { DEFAULT_RATIO, weight } from "./rules";
import { classifyAccount, classifyValues } from "./classification";
import { isFueRelevantSid, licensingBySid } from "../data/licensing";
import { personTraceFor } from "./trace";

export interface RawInput {
  persons: Person[];               // Grundgerüst: id, type, unit, accountIds, landscapeIds, primaryLandscape
  accounts: Account[];             // mit compositeRoleIds/singleRoleIds/isDev/trace/lastLogon
  singleRoles: SingleRole[];
  compositeRoles: CompositeRole[];
  /** Trace je Konto UND Einzelrolle (Schlüssel `${accountId}|${singleRoleId}`). Fehlt er, gilt der Konto-Trace. */
  roleTrace?: Map<string, TraceState>;
  /** Fachlicher Cluster je Rollen-Vorlage (für Maßnahmen). */
  clusterOf?: (template: string) => LeverCluster | undefined;
  rationaleOf?: (template: string) => string | undefined;
  now?: number;
}

export interface BuiltData {
  persons: Person[];
  accounts: Account[];
  levers: Lever[];
  roles: Role[];
}

export function buildDataset(input: RawInput): BuiltData {
  const { persons, accounts, singleRoles, compositeRoles } = input;
  const now = input.now ?? Date.now();
  const singleById = new Map(singleRoles.map((r) => [r.id, r]));
  const compById = new Map(compositeRoles.map((c) => [c.id, c]));
  const accById = new Map(accounts.map((a) => [a.id, a]));
  // Trace gibt es je Benutzer und System, nie je Rolle: Liegt für das Konto eine Aufzeichnung vor,
  // ist jede Rolle darauf „genutzt" oder „nie genutzt" — „kein Trace" für eine einzelne Rolle
  // wäre ein Widerspruch und wird zu „nie genutzt" (kein Aufruf im Zeitraum).
  const traceOf = (a: Account, roleId: string): TraceState => {
    const t = input.roleTrace?.get(`${a.id}|${roleId}`);
    if (t === undefined) return a.trace;
    if (t === "kein-trace" && a.trace !== "kein-trace") return "nie-genutzt";
    return t;
  };

  const singleIdsOf = (a: Account): Set<string> => {
    const ids = new Set<string>(a.singleRoleIds);
    for (const cid of a.compositeRoleIds) for (const sr of compById.get(cid)?.singleRoleIds ?? []) ids.add(sr);
    return ids;
  };

  /** Klasse eines Kontos aus seinem AKTUELLEN Rollenstand. */
  function reclassifyAccount(a: Account) {
    const values: AuthValue[] = [];
    const engines = new Set<EngineMetric>(a.engines);
    for (const sid of singleIdsOf(a)) {
      const sr = singleById.get(sid); if (!sr) continue;
      values.push(...sr.authValues);
      for (const e of sr.engines) engines.add(e);
    }
    const c = classifyAccount(values, a.isDev, isFueRelevantSid(a.sid) && singleIdsOf(a).size > 0);
    a.accountClass = c.cls; a.accountStep = c.step; a.engines = [...engines];
  }
  /** Klasse ohne die entfernbaren Einzelrollen (für cleanClass). */
  function accountCleanClass(a: Account): { cls: LicenseClass; step: number } {
    const values: AuthValue[] = [];
    const ids = singleIdsOf(a);
    for (const sid of ids) { const sr = singleById.get(sid); if (!sr || sr.removable) continue; values.push(...sr.authValues); }
    // Das Konto bleibt nach dem Rückbau ein Dialogbenutzer — Untergrenze Self-Service.
    const c = classifyAccount(values, a.isDev, isFueRelevantSid(a.sid) && ids.size > 0);
    return { cls: c.cls, step: c.step };
  }
  function setLastLogon(p: Person, accs: Account[]) {
    let letzte: number | null = null;
    for (const a of accs) { if (!a.lastLogon) continue; const t = Date.parse(a.lastLogon); if (Number.isFinite(t) && (letzte === null || t > letzte)) letzte = t; }
    p.lastLogonDays = letzte === null ? undefined : Math.max(0, Math.floor((now - letzte) / 86_400_000));
  }

  // AP1: Klasse, Bedarf und Treiber entstehen AUSSCHLIESSLICH aus FUE-relevanten Konten.
  function finalizePerson(p: Person) {
    const accs = p.accountIds.map((id) => accById.get(id)!).filter(Boolean);
    p.systemClasses = accs.map((a): SystemClass => ({
      sid: a.sid, client: a.client, accountId: a.id, cls: a.accountClass, engines: a.engines,
      fueRelevant: isFueRelevantSid(a.sid), licenseModel: licensingBySid(a.sid).model, trace: a.trace,
    }));
    const fueAccs = accs.filter((a) => isFueRelevantSid(a.sid));
    if (fueAccs.length === 0) {
      p.licenseClass = "NotRelevant"; p.cleanClass = "NotRelevant"; p.fue = 0;
      p.drivingSystem = undefined; p.drivingAccountId = undefined; p.singleSystemDriven = false;
      p.drivers = []; p.leverIds = []; setLastLogon(p, accs); return;
    }
    let driving = fueAccs[0];
    for (const a of fueAccs) if (a.accountStep < driving.accountStep) driving = a;
    p.licenseClass = driving.accountClass; p.drivingSystem = driving.sid; p.drivingAccountId = driving.id;
    p.fue = weight(p.licenseClass, DEFAULT_RATIO);
    let cleanStep = 9999, cleanCls: LicenseClass = "NotRelevant";
    for (const a of fueAccs) { const cc = accountCleanClass(a); if (cc.step < cleanStep) { cleanStep = cc.step; cleanCls = cc.cls; } }
    p.cleanClass = cleanCls;
    const topStep = driving.accountStep;
    p.singleSystemDriven = p.licenseClass !== p.cleanClass && fueAccs.filter((a) => a.accountStep === topStep).length === 1;

    const drivers: Driver[] = [];
    for (const a of fueAccs) {
      if (a.isDev) drivers.push({
        roleId: "#DEV#", roleName: "Developer-Kennzeichen (#DEV#)", authObject: "#DEV#", field: "", value: "",
        forcedClass: "Developer", trace: a.trace, landscapeId: p.primaryLandscape, sid: a.sid, accountId: a.id, singleRoleId: "#DEV#", ruleStep: 10,
      });
      for (const sid of singleIdsOf(a)) {
        const sr = singleById.get(sid);
        if (!sr || sr.forcedClass === "NotRelevant") continue;
        let av = sr.authValues[0], best = 9999;
        for (const v of sr.authValues) { const c = classifyValues([v]); if (c.step < best) { best = c.step; av = v; } }
        const comp = a.compositeRoleIds.find((cid) => compById.get(cid)?.singleRoleIds.includes(sid));
        drivers.push({
          roleId: sr.id, roleName: sr.name, authObject: av.obj, field: av.field ?? "", value: av.val ?? "",
          forcedClass: sr.forcedClass, trace: traceOf(a, sid), landscapeId: sr.landscapeId,
          removable: sr.removable, leverId: sr.leverId,
          sid: a.sid, accountId: a.id, compositeRoleId: comp, singleRoleId: sr.id, ruleStep: sr.forcedStep,
        });
      }
    }
    p.drivers = drivers;
    setLastLogon(p, accs);
    const leverIds: string[] = [];
    if (p.licenseClass !== p.cleanClass) for (const d of drivers) if (d.removable && d.ruleStep === topStep && d.leverId && !leverIds.includes(d.leverId)) leverIds.push(d.leverId);
    p.leverIds = leverIds;
  }

  for (const a of accounts) reclassifyAccount(a);
  for (const p of persons) finalizePerson(p);

  // Maßnahmen — je entfernbarer Rolle EINE; Aufwand und Handlung aus der Trace-Mischung.
  const levers: Lever[] = [];
  const carriersByLever = new Map<string, Person[]>();
  for (const p of persons) { const seen = new Set<string>(); for (const d of p.drivers) if (d.leverId && !seen.has(d.leverId)) { seen.add(d.leverId); (carriersByLever.get(d.leverId) ?? carriersByLever.set(d.leverId, []).get(d.leverId)!).push(p); } }
  for (const sr of singleRoles) {
    if (!sr.removable || !sr.leverId) continue;
    const leverId = sr.leverId; const carriers = carriersByLever.get(leverId) ?? [];
    let affected = 0, effective = 0, deltaFue = 0, traegerNie = 0, traegerGenutzt = 0;
    const byTrace: Record<TraceState, { persons: number; fue: number }> = { "nie-genutzt": { persons: 0, fue: 0 }, genutzt: { persons: 0, fue: 0 }, "kein-trace": { persons: 0, fue: 0 } };
    for (const p of carriers) {
      affected++;
      // Trace je Person = strengster Befund über alle ihre Konten (trace.ts) — dieselbe Regel wie im Plan.
      const ct = personTraceFor(p, leverId) ?? "kein-trace";
      if (ct === "nie-genutzt") traegerNie++; else if (ct === "genutzt") traegerGenutzt++;
      if (p.leverIds.includes(leverId)) {
        effective++;
        const r = weight(p.licenseClass, DEFAULT_RATIO) - weight(p.cleanClass, DEFAULT_RATIO);
        deltaFue += r;
        byTrace[ct].persons++; byTrace[ct].fue += r;
      }
    }
    if (affected === 0 || effective === 0) continue;
    // Nutzung über ALLE Träger: Ob jemand den Wert braucht, ist unabhängig davon, ob sein Entzug
    // die Person fallen lässt. Deshalb nicht über die wirksamen, sondern über die betroffenen.
    const unusedShare = traegerNie / affected;
    const usedShare = traegerGenutzt / affected;
    const effort: Lever["effort"] = unusedShare >= 0.7 ? "niedrig" : unusedShare >= 0.4 ? "mittel" : "hoch";
    // Die Handlung nennt den TECHNISCHEN Rollennamen (Z_ASP_FI_VOLL_16): eindeutig, und der Kurztext
    // kann fehlen. So liest es ein Berechtigungsteam — und so steht es im Ticket.
    const action = effort === "niedrig" ? `Wert aus der Rolle ${sr.id} entfernen`
      : effort === "mittel" ? `Rolle ${sr.id} splitten — Wert nur für die tatsächlichen Nutzer`
      : `Rolle ${sr.id} neu schneiden — kopieren, tauschen, Prozess abstimmen`;
    const trace: TraceState = byTrace["nie-genutzt"].persons >= byTrace.genutzt.persons && byTrace["nie-genutzt"].persons >= byTrace["kein-trace"].persons ? "nie-genutzt" : byTrace.genutzt.persons >= byTrace["kein-trace"].persons ? "genutzt" : "kein-trace";
    let av = sr.authValues[0], best = 9999;
    for (const v of sr.authValues) { const c = classifyValues([v]); if (c.step < best) { best = c.step; av = v; } }
    levers.push({
      id: leverId, label: sr.name || sr.id, authObject: av.obj, field: av.field ?? "", value: av.val ?? "",
      roleId: sr.id, roleName: sr.name, landscapeId: sr.landscapeId, sid: sr.sid,
      cluster: input.clusterOf?.(sr.template) ?? "Büro", action, unusedShare,
      affected, effective, deltaFue, trace, byTrace, safeFue: byTrace["nie-genutzt"].fue, effort,
      rationale: `${input.rationaleOf?.(sr.template) ?? ""} Auf ${sr.sid}: ${Math.round(unusedShare * 100)} % der wirksamen Personen haben den Wert nie genutzt, ${Math.round(usedShare * 100)} % nachweislich genutzt.`.trim(),
    });
  }

  // Rollen-Liste mit echten Trägerzahlen, ohne tote Rollen.
  const holders = new Map<string, Set<string>>();
  for (const a of accounts) {
    if (!a.personId.startsWith("P-")) continue;
    const ids = new Set<string>(a.singleRoleIds);
    for (const cid of a.compositeRoleIds) { ids.add(cid); for (const sr of compById.get(cid)?.singleRoleIds ?? []) ids.add(sr); }
    for (const rid of ids) (holders.get(rid) ?? holders.set(rid, new Set()).get(rid)!).add(a.personId);
  }
  const n = (id: string) => holders.get(id)?.size ?? 0;
  const roles: Role[] = [
    ...compositeRoles.map((c) => ({ id: c.id, name: c.name, landscapeId: c.landscapeId, sid: c.sid, kind: "sammel" as const, holders: n(c.id) })),
    ...singleRoles.map((s) => ({ id: s.id, name: s.name, landscapeId: s.landscapeId, sid: s.sid, kind: "einzel" as const, holders: n(s.id) })),
  ].filter((r) => r.holders > 0);

  return { persons, accounts, levers, roles };
}
